import React, { useEffect, useState } from 'react'
import { MdClose } from 'react-icons/md'
import paymentMethodService from '@/services/paymentMethodService'
import '@/styles/components/direct-debit-alert.css'

/**
 * Dismissal is remembered for the session only, not for good.
 *
 * Without a Direct Debit the customer's deliveries eventually stop, so this
 * is not an advert to be turned off permanently. Closing it clears the page
 * they are on; it comes back next visit, and disappears for good the moment a
 * payment method exists.
 */
const DISMISSED_KEY = 'portal:dd-alert-dismissed'

const DirectDebitAlert: React.FC = () => {
  const [hasPaymentMethod, setHasPaymentMethod] = useState<boolean | null>(null)
  const [ctaLoading, setCtaLoading] = useState(false)
  const [ctaError, setCtaError] = useState<string | null>(null)
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem(DISMISSED_KEY) === '1'
    } catch {
      // Private browsing can refuse sessionStorage. Showing the banner is the
      // right failure here: it is the more useful of the two states.
      return false
    }
  })

  function dismiss(): void {
    setDismissed(true)
    try {
      sessionStorage.setItem(DISMISSED_KEY, '1')
    } catch {
      /* see above */
    }
  }

  useEffect(() => {
    let cancelled = false
    paymentMethodService
      .getPrimary()
      .then((result) => {
        if (cancelled) return
        setHasPaymentMethod(result?.has_payment_method === true)
      })
      .catch(() => {
        if (!cancelled) setHasPaymentMethod(null)
      })
    return () => {
      cancelled = true
    }
  }, [])

  if (hasPaymentMethod !== false || dismissed) return null

  async function handleSetUpDirectDebit() {
    setCtaLoading(true)
    setCtaError(null)
    try {
      const url = await paymentMethodService.getManagedPageUrl()
      window.open(url, '_blank', 'noopener,noreferrer')
    } catch {
      setCtaError('Unable to open payment setup page. Please try again.')
    } finally {
      setCtaLoading(false)
    }
  }

  return (
    <div className="direct-debit-alert" role="status">
      <button
        type="button"
        className="direct-debit-alert-close"
        onClick={dismiss}
        aria-label="Dismiss this message"
      >
        <MdClose aria-hidden="true" />
      </button>

      <div className="direct-debit-alert-icon" aria-hidden="true">
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="24" height="24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
      <div className="direct-debit-alert-content">
        <h4 className="direct-debit-alert-title">Keep your deliveries flowing smoothly</h4>
        <p className="direct-debit-alert-message">
          Set up a Direct Debit today and we&apos;ll take care of the rest. It&apos;s the easiest way to make sure your milk and groceries arrive without interruption, leaving you one less thing to think about.
        </p>
        {ctaError && <p className="direct-debit-alert-error">{ctaError}</p>}
        <button
          type="button"
          className="direct-debit-alert-cta"
          onClick={handleSetUpDirectDebit}
          disabled={ctaLoading}
        >
          {ctaLoading ? 'Loading…' : 'Set Up Direct Debit'}
        </button>
      </div>
    </div>
  )
}

export default DirectDebitAlert
